<template>
  <div
    class="deed-container"
    :class="{ 'is-dragging': isDragging }"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="onDrop"
  >
    <div class="section-header">
      <h3>Title Deed</h3>
    </div>

    <input
      type="file"
      ref="uploadInput"
      accept="image/*,application/pdf"
      @change="onFileSelected"
      style="display: none"
    />

    <div v-show="error" class="upload-error">{{ error }}</div>

    <div v-if="isUploading" class="dropzone-empty">
      <b-spinner small class="mb-1" />
      <p class="dropzone-message">Uploading…</p>
    </div>

    <div v-else-if="!deed" class="dropzone-empty" @click="append">
      <feather-icon icon="UploadCloudIcon" size="36" class="dropzone-icon" />
      <p class="dropzone-message">Click to upload or drop the title deed here</p>
      <small class="text-muted">PNG, JPG or PDF</small>
    </div>

    <div v-else class="deed-preview">
      <img
        v-if="isImage"
        :src="deed.src"
        class="deed-thumb"
        draggable="false"
        @click="openPreview"
      />
      <div v-else class="deed-thumb deed-file-icon" @click="openPreview">
        <feather-icon icon="FileTextIcon" size="32" />
      </div>

      <div class="deed-file-info">
        <span class="deed-file-name">{{ deed.filename }}</span>
      </div>

      <div class="deed-actions">
        <button
          v-b-tooltip.hover
          title="Preview"
          type="button"
          class="icon-btn"
          @click="openPreview"
        >
          <feather-icon icon="EyeIcon" size="15" />
        </button>

        <button
          v-b-tooltip.hover
          title="Replace"
          type="button"
          class="icon-btn"
          @click="append"
        >
          <feather-icon icon="RefreshCwIcon" size="15" />
        </button>

        <button
          v-b-tooltip.hover
          title="Remove"
          type="button"
          class="icon-btn icon-btn-danger"
          @click="remove"
        >
          <feather-icon icon="XIcon" size="15" />
        </button>
      </div>
    </div>

    <div v-if="showPreview" class="fullscreen-modal" @click.self="closePreview">
      <div v-if="!isImage" class="fullscreen-pdf-wrap">
        <iframe :src="deed.src" class="fullscreen-pdf" title="Title deed preview" />
        <a :href="deed.src" target="_blank" rel="noopener" class="open-new-tab-link">
          Not rendering? Open in a new tab
        </a>
      </div>
      <img v-else :src="deed.src" class="fullscreen-image" draggable="false" />

      <button class="close-btn" @click="closePreview">×</button>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import { BSpinner, VBTooltip } from 'bootstrap-vue'

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif']

export default {
  name: 'UploadTitleDeed',
  components: {
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      error: '',
      isDragging: false,
      isUploading: false,
      deed: null,
      showPreview: false,
    }
  },
  computed: {
    isImage() {
      const ext = this.deed?.filename?.split('.').pop()?.toLowerCase()
      return IMAGE_EXTENSIONS.includes(ext)
    },
  },
  created() {
    this.fetchDeed()
  },
  methods: {
    async fetchDeed() {
      try {
        const { data } = await axios.get(`property/v1/gettitledeed/${this.id}`)
        this.deed = data?.data || null
      } catch (err) {
        console.error('Error fetching title deed:', err)
      }
    },
    append() {
      this.$refs.uploadInput.click()
    },
    onFileSelected(event) {
      const [selected] = event.target.files
      if (selected) this.setFile(selected)
    },
    onDrop(event) {
      this.isDragging = false
      const [dropped] = event.dataTransfer.files
      if (dropped) this.setFile(dropped)
    },
    async setFile(rawFile) {
      const isAccepted = rawFile.type.startsWith('image') || rawFile.type === 'application/pdf'

      if (!isAccepted) {
        this.error = 'Unsupported file type. Please upload an image or PDF.'
        return
      }

      this.error = ''
      this.isUploading = true

      const formData = new FormData()
      formData.append('file', rawFile)

      try {
        await axios.post(`property/v1/upload_titledeed/${this.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        await this.fetchDeed()
      } catch (err) {
        this.error = err.response?.data?.message || 'Upload failed. Please try again.'
        console.error('Error uploading title deed:', err)
      } finally {
        this.isUploading = false
        this.$refs.uploadInput.value = null
      }
    },
    async remove() {
      if (!this.deed) return

      try {
        await axios.delete(`property/v1/deletetitledeed/${this.deed.id}`)
        this.deed = null
        this.showPreview = false
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to remove file. Please try again.'
        console.error('Error deleting title deed:', err)
      }
    },
    openPreview() {
      this.showPreview = true
    },
    closePreview() {
      this.showPreview = false
    },
  },
}
</script>

<style scoped>
.deed-container {
  width: 100%;
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  padding: 20px;
  position: relative;
  transition: border-color 0.15s, background-color 0.15s;
}
.deed-container.is-dragging {
  border: 2px dashed #7367f0;
  background-color: rgba(115, 103, 240, 0.04);
}
.upload-error {
  text-align: center;
  color: #ea5455;
  font-size: 15px;
  margin-bottom: 10px;
}
.dropzone-empty {
  position: relative;
  text-align: center;
  padding: 40px 20px;
  border: 2px dashed #d8dbe0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}
.dropzone-empty:hover {
  border-color: #7367f0;
  background-color: rgba(115, 103, 240, 0.03);
}
.dropzone-icon {
  color: #a3a8b1;
  display: block;
  margin: 0 auto 10px;
}
.dropzone-message {
  margin: 0 0 4px;
  font-weight: 500;
}
.deed-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  padding: 14px 16px;
}
.deed-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
  cursor: pointer;
}
.deed-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f1fb;
  color: #7367f0;
}
.deed-file-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
}
.deed-file-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #303542;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.deed-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.icon-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9198a5;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}
.icon-btn:hover {
  background: #f1f2f6;
  color: #5e6472;
}
.icon-btn-danger:hover {
  background: rgba(234, 84, 85, 0.1);
  color: #ea5455;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.section-header h3 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5e6472;
  margin: 0;
}
.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.fullscreen-image {
  width: 100%;
  height: auto;
  max-width: 1280px;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
}
.fullscreen-pdf-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.fullscreen-pdf {
  width: 90vw;
  height: 85vh;
  max-width: 1000px;
  border: none;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
}
.open-new-tab-link {
  color: #fff;
  text-decoration: underline;
  font-size: 0.9rem;
}
.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 2.5rem;
  color: white;
  text-shadow: 0 0 5px black;
  cursor: pointer;
  z-index: 10000;
  user-select: none;
}
</style>
